import React, { useEffect, useState, useCallback } from "react";
import { Bell, CheckCheck, CircleDot } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import api from "@/lib/apiClient";

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/notifications");
      setItems(res.data.items);
      setUnread(res.data.unread);
    } catch (e) {}
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const markAll = async () => {
    await api.post("/notifications/read-all");
    load();
  };

  const kindColor = { success: "text-emerald-600", error: "text-red-600", info: "text-blue-600" };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="notification-bell">
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 h-5 w-5 rounded-full bg-primary text-[10px] font-bold text-white flex items-center justify-center" data-testid="notification-unread-count">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0" data-testid="notification-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h4 className="font-display font-bold text-sm">Notifications</h4>
          <Button variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={markAll} data-testid="mark-all-read-button">
            <CheckCheck className="h-3.5 w-3.5" /> Mark all read
          </Button>
        </div>
        <ScrollArea className="h-96">
          {items.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">No notifications yet.</div>
          ) : (
            items.map((n) => (
              <div key={n.id} className={`px-4 py-3 border-b last:border-0 flex gap-3 ${n.read ? "opacity-60" : "bg-accent/30"}`} data-testid={`notification-item-${n.id}`}>
                <CircleDot className={`h-4 w-4 mt-0.5 shrink-0 ${kindColor[n.kind] || "text-slate-500"}`} />
                <div className="min-w-0">
                  <p className="text-sm font-semibold truncate">{n.title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{n.message}</p>
                  <p className="text-[10px] text-muted-foreground/70 mt-1">{new Date(n.created_at).toLocaleString()}</p>
                </div>
              </div>
            ))
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
